import { useQuery, useMutation, useQueryClient } from "react-query";
import { healthFormsApi, QuestionListItem, BookListItem, QuestionGroupListItem } from "./api";
import { Question } from "./types/common";
import { Book, QuestionGroup } from "./types/persisted";

export const queryKeys = {
  questions: ["healthForms", "questions"] as const,
  question: (id: number) => ["healthForms", "questions", id] as const,
  books: ["healthForms", "books"] as const,
  book: (id: number) => ["healthForms", "books", id] as const,
  questionGroups: ["healthForms", "questionGroups"] as const,
  questionGroup: (id: number) => ["healthForms", "questionGroups", id] as const,
};

export const useQuestions = () =>
  useQuery<QuestionListItem[]>(queryKeys.questions, healthFormsApi.questions.list);

export const useQuestion = (id: number | null) =>
  useQuery<Question>(queryKeys.question(id ?? 0), () => healthFormsApi.questions.get(id!), {
    enabled: !!id,
  });

export const useUpdateQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, question }: { id: number; question: Question }) =>
      healthFormsApi.questions.update(id, question),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(queryKeys.questions);
        queryClient.invalidateQueries(queryKeys.question(id));
      },
    }
  );
};

export const useDeleteQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.questions.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.questions);
    },
  });
};

export const useBooks = () => useQuery<BookListItem[]>(queryKeys.books, healthFormsApi.books.list);

export const useAllBooks = () =>
  useQuery<Book[]>(
    [...queryKeys.books, "full"] as const,
    async () => {
      const list = await healthFormsApi.books.list();
      return Promise.all(list.map((b) => healthFormsApi.books.get(b.id)));
    }
  );

export const useBook = (id: number | null) =>
  useQuery<Book>(queryKeys.book(id ?? 0), () => healthFormsApi.books.get(id!), {
    enabled: !!id,
  });

export const useUpdateBook = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, book }: { id: number; book: Book }) => healthFormsApi.books.update(id, book),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(queryKeys.books);
        queryClient.invalidateQueries(queryKeys.book(id));
      },
    }
  );
};

export const useDeleteBook = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.books.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.books);
    },
  });
};

export const useQuestionGroups = () =>
  useQuery<QuestionGroupListItem[]>(queryKeys.questionGroups, healthFormsApi.questionGroups.list);

export const useAllQuestionGroups = () =>
  useQuery<QuestionGroup[]>(
    [...queryKeys.questionGroups, "full"] as const,
    async () => {
      const list = await healthFormsApi.questionGroups.list();
      return Promise.all(list.map((qg) => healthFormsApi.questionGroups.get(qg.id)));
    }
  );

export const useQuestionGroup = (id: number | null) =>
  useQuery<QuestionGroup>(
    queryKeys.questionGroup(id ?? 0),
    () => healthFormsApi.questionGroups.get(id!),
    {
      enabled: !!id,
    }
  );

export const useUpdateQuestionGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, group }: { id: number; group: QuestionGroup }) =>
      healthFormsApi.questionGroups.update(id, group),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(queryKeys.questionGroups);
        queryClient.invalidateQueries(queryKeys.questionGroup(id));
      },
    }
  );
};

export const useDeleteQuestionGroup = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.questionGroups.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.questionGroups);
    },
  });
};

export const useCreateQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (question: Omit<Question, "id" | "hash" | "updatedAt">) => healthFormsApi.questions.create(question),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.questions);
      },
    }
  );
};

export const useCreateBook = () => {
  const queryClient = useQueryClient();
  return useMutation((book: Omit<Book, "id" | "updatedAt">) => healthFormsApi.books.create(book), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.books);
    },
  });
};

export const useCreateQuestionGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (group: Omit<QuestionGroup, "id" | "updatedAt">) => healthFormsApi.questionGroups.create(group),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.questionGroups);
      },
    }
  );
};
